import * as React from 'react';
import {
	Stack,
	TextField,
	FormControlLabel,
	Switch,
	Button,
	CircularProgress,
	IconButton,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import { ChatInputProps } from './types';
import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl';
import AiProviderSelector from './ProviderSelector';
import { useLocalStorage } from '../../../../../utils/useLocalStorage';
import SearchPrompt from '../../promptBank/SearchPrompt';
import { PromptDataModel } from 'services/promptApi';
import { toast } from 'react-toastify';

export const ChatInput = ({
	value,
	onChange,
	onSend,
	loading,
	autoInsert,
	onAutoInsertChange,
	onClear,
	lockedProvider = undefined,
	setValue,
	onStop,
}: ChatInputProps) => {
	const intl = useIntl();
	const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
	const [provider, setProvider] = useLocalStorage('aiProvider', 'openai');
	const [openAiModel, setOpenAiModel] = useLocalStorage('openAIModel', 'gpt-4.1');
	const [geminiModel, setGeminiModel] = useLocalStorage('geminiModel', 'gemini-2.0-flash');
	const model = provider === 'openai' ? openAiModel : geminiModel;

	const handleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			onSend(model, provider);
		}
	};

	const handleClearClick = () => {
		setShowConfirmDialog(true);
	};

	const handleConfirmClear = () => {
		onClear();
		setShowConfirmDialog(false);
	};

	const handleCancelClear = () => {
		setShowConfirmDialog(false);
	};

	const handleProviderModelChange = (newProvider: string, newModel: string) => {
		setProvider(newProvider);
		if (newProvider === 'openai') {
			setOpenAiModel(newModel);
		} else {
			setGeminiModel(newModel);
		}
	};

	return (
		<>
			<Stack direction='column' spacing={1} sx={{ width: '100%' }}>
				<TextField
					fullWidth
					multiline
					rows={4}
					value={value}
					onChange={e => onChange(e.target.value)}
					onKeyPress={handleKeyPress}
					placeholder={intl.formatMessage({ id: 'chat.typeMessage' })}
					variant='outlined'
					size='small'
					disabled={loading}
				/>
				<Stack direction='row' spacing={2} alignItems='center' justifyContent='space-between' sx={{ width: '100%' }}>
					<Stack direction='row' spacing={1} alignItems='center'>
						<FormControlLabel
							control={<Switch checked={autoInsert} onChange={e => onAutoInsertChange(e.target.checked)} color='primary' />}
							label={<FormattedMessage id='chat.autoInsert' />}
						/>
						<Tooltip title={<FormattedMessage id='chat.clearAll' defaultMessage='Clear all messages and selections' />}>
							<IconButton onClick={handleClearClick} color='error' size='small'>
								<DeleteIcon />
							</IconButton>
						</Tooltip>
					</Stack>

					<SearchPrompt
						getSelectedPrompt={(data: PromptDataModel) => {
							if (!data || !data.promptContent) {
								toast.error('');
							}

							setValue(data.promptContent);
						}}
					/>

					<AiProviderSelector onProviderChange={handleProviderModelChange} provider={lockedProvider} />
					{loading && onStop ? (
						<Button variant='contained' color='error' onClick={onStop} startIcon={<StopIcon />} sx={{ ml: 'auto' }}>
							<FormattedMessage id='chat.stop' defaultMessage='Stop' />
						</Button>
					) : (
						<Button
							variant='contained'
							color='primary'
							onClick={() => onSend(model, provider)}
							disabled={loading || !value.trim()}
							startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
							sx={{ ml: 'auto' }}
						>
							<FormattedMessage id='chat.send' />
						</Button>
					)}
				</Stack>
			</Stack>

			<Dialog open={showConfirmDialog} onClose={handleCancelClear} aria-labelledby='clear-confirm-dialog-title'>
				<DialogTitle id='clear-confirm-dialog-title'>
					<FormattedMessage id='chat.clearConfirmTitle' defaultMessage='Clear Chat' />
				</DialogTitle>
				<DialogContent>
					<FormattedMessage
						id='chat.clearConfirmMessage'
						defaultMessage='Are you sure you want to clear all messages and selections? This action cannot be undone.'
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelClear} color='primary'>
						<FormattedMessage id='common.cancel' defaultMessage='Cancel' />
					</Button>
					<Button onClick={handleConfirmClear} color='error' variant='contained'>
						<FormattedMessage id='common.clear' defaultMessage='Clear' />
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
